import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useGetAttributeListing from "./attributes/useGetAttributeListing";
import useProductConfiguration from "../useProductConfiguration";

const useSelectAttributeLogic = () => {
  const [rows, setRows] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Get selected attributes from context
  const {
    selectedAttributes,
    toggleSelectedAttribute,
    setAllSelectedAttributes,
    clearSelectedAttributes,
    isAttributeSelected,
    getSelectedAttributeIds,
  } = useProductConfiguration();

  const allIds = useMemo(() => rows.map((r) => r.attribute_id), [rows]);
  const selectedIds = useMemo(
    () => getSelectedAttributeIds(),
    [selectedAttributes]
  );
  const headerCheckboxRef = useRef(null);

  const allChecked = selectedIds.length === rows.length && rows.length > 0;
  const noneChecked = selectedIds.length === 0;
  const isIndeterminate = !allChecked && !noneChecked;

  const { isAttributeListLoading } = useGetAttributeListing({
    limit: 100,
    onSuccess: (data) => {
      setRows(data?.data);

      // Handle pre-population from EditConfig if data exists
      if (window.tempPrePopulateData && data?.data) {
        const { attributeNames } = window.tempPrePopulateData;
        console.log(
          "Processing pre-population with attribute names:",
          attributeNames
        );

        // Find matching attributes by name
        const matchingAttributes = data.data.filter((attr) =>
          attributeNames.includes(attr.attribute_name)
        );

        console.log("Found matching attributes:", matchingAttributes);

        if (matchingAttributes.length > 0) {
          setAllSelectedAttributes(matchingAttributes);
        }

        // Clean up the temporary data
        delete window.tempPrePopulateData;
        return;
      }

      // Update selected attributes with fresh data when it arrives (existing logic)
      if (selectedAttributes.length > 0 && data?.data) {
        const freshSelectedAttributes = [];
        selectedAttributes.forEach((selectedAttr) => {
          const freshAttr = data.data.find(
            (attr) => attr.attribute_id === selectedAttr.attribute_id
          );
          if (freshAttr) {
            freshSelectedAttributes.push(freshAttr);
          }
        });

        // Only update if there are changes (to avoid infinite loops)
        const hasChanges = freshSelectedAttributes.some((freshAttr, index) => {
          const currentAttr = selectedAttributes[index];
          return (
            !currentAttr ||
            freshAttr.values?.length !== currentAttr.values?.length ||
            JSON.stringify(freshAttr.values) !==
              JSON.stringify(currentAttr.values)
          );
        });

        if (hasChanges) {
          setAllSelectedAttributes(freshSelectedAttributes);
        }
      }
    },
  });

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleAll = () => {
    selectedIds.length === rows.length
      ? clearSelectedAttributes()
      : setAllSelectedAttributes([...rows]);
  };

  const toggleOne = (attributeObject) => {
    toggleSelectedAttribute(attributeObject);
  };

  const handleEdit = (row) => {
    setIsEditModalOpen(true);
    setEditData(row);
  };

  const handleCreateNewAttribute = () => {
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditData(null);
  };

  // Debug function to log selected attributes (you can remove this later)
  const logSelectedAttributes = () => {
    console.log("Selected Attributes (Full Objects):", selectedAttributes);
    console.log("Selected IDs only:", selectedIds);
  };

  return {
    // Data
    rows,
    selectedAttributes,
    selectedIds,
    allIds,
    headerCheckboxRef,

    // Computed values
    allChecked,
    noneChecked,
    isIndeterminate,
    isAttributeListLoading,

    // Modal states
    isCreateModalOpen,
    isEditModalOpen,
    editData,

    // Actions
    toggleAll,
    toggleOne,
    isAttributeSelected,
    logSelectedAttributes,
    handleEdit,
    handleCreateNewAttribute,
    handleCloseCreateModal,
    handleCloseEditModal,
  };
};

export default useSelectAttributeLogic;
